import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, CalendarRange, RotateCcw, CloudDownload, Lock, Database, AlertTriangle } from "lucide-react";
import { Badge } from "../Primitives";
import { OriginBadge } from "../../lib/origin.jsx";
import { useRole } from "../../lib/role.jsx";
import { relTime } from "../apikeys/shared";
import { SearchBox, Pager, useDebounced } from "../classification/ChangeParts";
import { getTableRows, listDataTables, refreshFromS3 } from "../../lib/dataTablesApi";
import DataGrid from "./DataGrid";
import ColumnPicker from "./ColumnPicker";
import RowDetail from "./RowDetail";
import { loadHidden, saveHidden, isNumeric } from "./format";
import { formatNumber, cn } from "../../lib/utils";

function KindBadge({ d }) {
  if (d.kind === "promoted") return <Badge status="success" testid="dt-kind">Promoted through Bronze review</Badge>;
  if (d.kind === "bronze") return <Badge status="warning" testid="dt-kind">Raw Bronze file</Badge>;
  if (d.kind === "empty") return <Badge status="neutral" testid="dt-kind">No rows yet</Badge>;
  if (d.kind === "actual" && d.practice) return <Badge status="warning" testid="dt-kind">Practice files · not real data</Badge>;
  if (d.kind === "actual") return <Badge status="success" testid="dt-kind">Real 1Rotary rows</Badge>;
  return <Badge status="info" testid="dt-kind">Sample rows</Badge>;
}

function SourceLine({ d, isIT, busy, onRefresh }) {
  const a = d.actual;
  if (d.source_note) return <span data-testid="dt-source-line" className="text-muted">{d.source_note}</span>;
  if (!a?.mapped) return <span data-testid="dt-source-line" className="text-muted">No 1Rotary S3 source is linked to this table yet.</span>;
  return (
    <span data-testid="dt-source-line" className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-muted">S3 source <span className="font-mono-tabular text-fg">{a.source}</span> ·</span>
      {a.loaded ? <span className="text-success">rows loaded {relTime(a.loaded_at)}</span>
        : <span className="text-warning inline-flex items-center gap-1"><AlertTriangle size={11} />waiting for S3{a.error ? ` — ${a.error}` : ""}</span>}
      {a.loaded && a.error && <span className="text-warning">· last check failed: {a.error}</span>}
      {isIT && (
        <button type="button" data-testid="dt-refresh-s3" onClick={onRefresh} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-border rounded-sm hover:border-primary hover:text-primary disabled:opacity-40">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <CloudDownload size={11} />} Reload from S3
        </button>
      )}
    </span>
  );
}

function DateRange({ field, from, to, onFrom, onTo }) {
  const input = "h-8 bg-bg border border-border rounded-sm px-2 text-[11.5px] font-mono-tabular focus:outline-none focus:border-primary disabled:opacity-40 dark:[color-scheme:dark]";
  return (
    <div className="inline-flex items-center gap-1.5 text-[11px] text-muted" title={field ? `Filters on ${field}` : "This table has no date field"}>
      <CalendarRange size={12} />
      <input type="date" data-testid="dt-date-from" value={from} onChange={e => onFrom(e.target.value)} disabled={!field} className={input} />
      <span>to</span>
      <input type="date" data-testid="dt-date-to" value={to} min={from || undefined} onChange={e => onTo(e.target.value)} disabled={!field} className={input} />
      <span className="hidden xl:inline font-mono-tabular">{field ? `on ${field}` : "no date field"}</span>
    </div>
  );
}

function Header({ name, d, onClose }) {
  return (
    <div className="sticky top-0 bg-bg/95 backdrop-blur-md border-b border-border px-4 md:px-5 py-3.5 flex items-start justify-between gap-3 z-20">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-1 inline-flex items-center gap-1.5">
          <Database size={11} /> Rows · {d?.table.layer || "…"}
        </div>
        <h2 className="font-mono-tabular text-lg font-semibold tracking-tight leading-tight truncate" data-testid="dt-title">{name}</h2>
        {d && <p className="text-[12px] text-muted mt-0.5">{d.table.description}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {d && <OriginBadge {...d.origin} testid="dt-origin" />}
        <button type="button" data-testid="dt-close" onClick={onClose}
                className="w-8 h-8 inline-flex items-center justify-center border border-border hover:border-primary hover:text-primary rounded-sm"><X size={14} /></button>
      </div>
    </div>
  );
}

// Drill-down for one Data Tables entry: every row, one search box, date range, sortable columns, row detail.
export default function TableDrillDrawer({ name, onClose }) {
  const { user } = useRole();
  const isIT = user?.role === "it_head";
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState(null);
  const [dir, setDir] = useState(null);
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const [hidden, setHidden] = useState(new Set());
  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState(false);

  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  useEffect(() => { setPage(1); }, [dq, from, to, sort, dir]);
  useEffect(() => { setRow(null); }, [dq, from, to, sort, dir, page]);
  useEffect(() => {
    let alive = true;
    let retry;
    setLoading(true);
    getTableRows(name, { q: dq, date_from: from, date_to: to, sort, dir, page, size: 50 })
      .then(x => { if (alive) { setD(x); setError(null); } })
      .catch(e => {
        if (!alive) return;
        setError(e);
        if (e.status === 503) retry = setTimeout(() => setNonce(n => n + 1), 4000);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; clearTimeout(retry); };
  }, [name, dq, from, to, sort, dir, page, nonce]);
  const kind = d?.kind;
  useEffect(() => { if (kind) setHidden(loadHidden(name, kind)); }, [name, kind]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { if (row) setRow(null); else onClose(); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [row, onClose]);

  const onSort = useCallback((key) => {
    const col = d?.columns.find(c => c.key === key);
    if (d?.sort === key) setDir(d.dir === "asc" ? "desc" : "asc");
    else { setSort(key); setDir(col && (isNumeric(col) || ["date", "datetime"].includes(col.type)) ? "desc" : "asc"); }
  }, [d]);
  const changeHidden = (set) => { setHidden(set); saveHidden(name, kind, set); };
  async function reload() {
    setBusy(true);
    setSort(null); setDir(null);
    try {
      await refreshFromS3(user);
      for (let i = 0; i < 120 && alive.current; i++) {
        await new Promise(r => setTimeout(r, 2000));
        if (!(await listDataTables()).refreshing) break;
      }
      if (alive.current) setNonce(n => n + 1);
    } catch (e) { if (alive.current) setError(e); }
    if (alive.current) setBusy(false);
  }
  const filtered = dq || from || to;
  const visible = d ? d.columns.filter(c => !hidden.has(c.key)) : [];

  return createPortal(
    <>
      <div data-testid="dt-backdrop" onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in" />
      <aside data-testid="dt-drawer" className="fixed top-0 right-0 h-screen w-full lg:w-[94vw] 2xl:w-[1480px] bg-bg border-l border-border shadow-2xl z-50 overflow-y-auto animate-slide-in-right">
        <Header name={name} d={d} onClose={onClose} />
        {!d && loading && <div className="py-16 text-center text-[12px] text-muted"><Loader2 size={14} className="inline animate-spin mr-2" />Loading rows…</div>}
        {!d && error && (
          <div data-testid="dt-error" className="m-5 text-[12px] border rounded-sm p-3 border-warning/40 bg-warning/5 text-warning">
            {error.message}{error.status === 503 && <Loader2 size={12} className="inline animate-spin ml-2" />}
          </div>
        )}
        {d && (
          <div className="p-3 md:p-5 space-y-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px]" data-testid="dt-info">
              <KindBadge d={d} />
              <span data-testid="dt-held"><strong className="font-mono-tabular">{formatNumber(d.rows_held)}</strong> rows held · {d.window?.label}
                {d.window?.start && <span className="text-muted font-mono-tabular"> ({d.window.start} → {d.window.end})</span>}
                {d.truncated && <span className="text-warning"> · capped at the latest 60,000 rows</span>}</span>
              {d.masked && d.columns.some(c => c.pii) && (
                <span data-testid="dt-masked-note" className="inline-flex items-center gap-1 text-warning"><Lock size={11} />Personal details masked — only the IT Head sees them in full</span>
              )}
            </div>
            <div className="text-[11px]"><SourceLine d={d} isIT={isIT} busy={busy} onRefresh={reload} /></div>
            <div className="flex flex-wrap items-center gap-2" data-testid="dt-toolbar">
              <SearchBox value={q} onChange={setQ} testid="dt-search" placeholder="Search every field" />
              <DateRange field={d.date_field} from={from} to={to} onFrom={setFrom} onTo={setTo} />
              {filtered && (
                <button type="button" data-testid="dt-clear" onClick={() => { setQ(""); setFrom(""); setTo(""); }}
                        className="inline-flex items-center gap-1 h-8 px-2.5 text-[11.5px] text-muted hover:text-primary"><RotateCcw size={11} /> Clear</button>
              )}
              <div className="flex-1" />
              {loading && <Loader2 size={13} className="animate-spin text-muted" data-testid="dt-loading" />}
              <span data-testid="dt-total" className="text-[11.5px] text-muted font-mono-tabular">{formatNumber(d.total)} {filtered ? "matching" : ""} rows</span>
              <ColumnPicker columns={d.columns} hidden={hidden} onChange={changeHidden} />
            </div>
            {error && <div data-testid="dt-error" className="text-[11.5px] text-critical">{error.message}</div>}
            <div className="tile overflow-hidden">
              <DataGrid columns={visible} rows={d.rows} sort={d.sort} dir={d.dir} masked={d.masked} onSort={onSort}
                        onOpen={setRow} openId={row?._rid} loading={loading} />
              {d.rows.length === 0 && <div data-testid="dt-empty" className={cn("py-10 text-center text-[12px] text-muted")}>No rows match these filters.</div>}
              <Pager data={d} onPage={setPage} testid="dt-page" />
            </div>
          </div>
        )}
      </aside>
      {d && <RowDetail row={row} columns={d.columns} masked={d.masked} onClose={() => setRow(null)} />}
    </>,
    document.body,
  );
}
